"use client";

import { useEffect } from "react";

import { useReportWebVitals } from "next/web-vitals";
import { APP_URL } from "@/config/urls";
import { IS_MULTI_TENANT } from "@/lib/tenancy";
import { resolveTenantKeyFromHost } from "@/lib/tenantKey";
import {
  getDeviceType,
  getEntryPath,
  getPageLoadId,
  getSessionId,
  getVisitorId,
  startNewPageLoad,
} from "./webVitalsIdentity";

/**
 * Real-user Core Web Vitals → POST /api/web-vitals.
 *
 * WHY THIS EXISTS. CrUX (what PSI shows as "field data") is a rolling 28-day window,
 * so it cannot tell you whether a fix deployed today worked. This reports the same
 * metrics from real visitors immediately.
 *
 * ── Delivery ────────────────────────────────────────────────────────────────────
 * Metrics are buffered and sent with navigator.sendBeacon, which the browser keeps
 * in flight after the page is gone — a plain fetch() is cancelled on unload, which is
 * exactly when CLS and LCP are finalized.
 *
 * The request is kept CORS-"simple" (text/plain body, no custom headers) so it never
 * triggers a preflight: the preflight OPTIONS cannot reliably complete during unload,
 * and the real request would be dropped with it. The tenant therefore rides in `?t=`
 * instead of X-Tenant-Key — see server/middleware/tenantDb.js.
 *
 * ── Why re-send on every hide ───────────────────────────────────────────────────
 * INP keeps rising while a visitor is still on the page, and CLS keeps accumulating.
 * A single send at first hide would miss anything after it. So the whole buffer goes
 * again on each hide, and the server upserts on (page_load_id, metric_name): the row
 * is corrected in place rather than duplicated, keeping the WORSE of the stored and the
 * incoming value. Not "last write wins": the first hide fires several beacons
 * milliseconds apart and they can land in any order (see upsertRows on the server).
 */

type Sample = Record<string, unknown>;

/** Buffered by metric name so a revised INP replaces the earlier one instead of stacking. */
const buffer = new Map<string, Sample>();

/** Signature of the last payload sent, so an unchanged buffer is not re-sent. */
let lastSentSignature = "";

/** Populated by the LCP observer below; merged in at send time. */
let lcpElement: string | null = null;
let lcpUrl: string | null = null;

/**
 * Device, connection, memory and viewport, captured ONCE per page load at its first send.
 *
 * Re-reading them on every beacon let one page load disagree with itself: a window resized
 * across 768px between hides filed some of its metrics as mobile and the rest as desktop,
 * because the server keeps whichever beacon inserted each row first.
 */
let context: Record<string, unknown> | null = null;

const readContext = () => {
  const nav = navigator as Navigator & {
    connection?: { effectiveType?: string };
    deviceMemory?: number;
  };
  return {
    device_type: getDeviceType(),
    effective_connection_type: nav.connection?.effectiveType ?? null,
    device_memory: nav.deviceMemory ?? null,
    viewport_width: window.innerWidth,
  };
};

/** 1 = report every visit. Set NEXT_PUBLIC_WEB_VITALS_SAMPLE_RATE=0 to switch RUM off. */
const SAMPLE_RATE = (() => {
  const raw = Number(process.env.NEXT_PUBLIC_WEB_VITALS_SAMPLE_RATE);
  return Number.isFinite(raw) && raw >= 0 && raw <= 1 ? raw : 1;
})();

/** Decided once per page load so a visitor is either fully sampled or not at all. */
let sampledIn: boolean | null = null;
const isSampledIn = () => (sampledIn ??= Math.random() < SAMPLE_RATE);

/** Short, human-readable identifier for the LCP element — "img.hero-bg", "h1#title". */
function describe(el: Element | null): string | null {
  if (!el) return null;
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : "";
  const cls =
    typeof el.className === "string" && el.className.trim()
      ? `.${el.className.trim().split(/\s+/).slice(0, 2).join(".")}`
      : "";
  return `${tag}${id}${cls}`.slice(0, 512);
}

/**
 * The LCP element and its URL, straight from the performance timeline.
 *
 * web-vitals/attribution would also provide this, but it is not exported by
 * next/web-vitals and deep-importing Next's bundled copy would tie us to a private
 * path. The native entry carries `element` and `url` directly, so this costs one
 * observer and no dependency — and it is the single most useful field here: it turns
 * "LCP was 4.2s" into "LCP was the accreditation image".
 */
function observeLcp() {
  if (typeof PerformanceObserver === "undefined") return;
  try {
    const po = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as
        | (PerformanceEntry & { element?: Element | null; url?: string })
        | undefined;
      if (!last) return;
      lcpElement = describe(last.element ?? null) ?? lcpElement;
      lcpUrl = last.url ? String(last.url).slice(0, 512) : lcpUrl;
    });
    po.observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
    /* unsupported browser — metrics still report, just without the element name */
  }
}

/** `${API_BASE_URL}/web-vitals?t=<tenant>`, or null when we must not send. */
function endpoint(): string | null {
  const base = String(APP_URL.API_BASE_URL ?? "").replace(/\/+$/, "");
  if (!base) return null;
  if (!IS_MULTI_TENANT) return `${base}/web-vitals`;
  // Multi-tenant: without a resolvable tenant the server has no database to write to,
  // so stay silent rather than send something it will reject.
  const key = resolveTenantKeyFromHost(window.location.host);
  return key ? `${base}/web-vitals?t=${encodeURIComponent(key)}` : null;
}

function flush() {
  if (!buffer.size || typeof navigator === "undefined") return;
  const url = endpoint();
  if (!url || typeof navigator.sendBeacon !== "function") return;

  context ??= readContext();
  const samples = Array.from(buffer.values()).map((s) => ({
    ...s,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    page_load_id: getPageLoadId(),
    url_path: getEntryPath(),
    // No page_type: the server looks the CMS page type up from url_path, and would ignore
    // a client-supplied one — the endpoint is public, the column is filtered on.
    ...context,
    ...(s.name === "LCP" ? { lcp_element: lcpElement, lcp_url: lcpUrl } : {}),
  }));

  // A visitor who alt-tabs repeatedly fires visibilitychange each time. Re-sending an
  // identical payload would be pure noise — the upsert would rewrite the same row — so
  // only send when a metric has actually appeared or changed since the last beacon.
  const signature = Array.from(buffer.entries())
    .map(([name, sample]) => `${name}:${sample.value}`)
    .join("|");
  if (signature === lastSentSignature) return;
  lastSentSignature = signature;

  try {
    // text/plain keeps this a simple request (see the header comment).
    navigator.sendBeacon(url, new Blob([JSON.stringify({ samples })], { type: "text/plain" }));
  } catch {
    /* telemetry must never surface an error to the visitor */
  }
}

/**
 * Module scope on purpose: useReportWebVitals lists its callback as a useEffect
 * dependency, so an inline arrow would re-subscribe on every render and report each
 * metric several times over.
 */
function record(metric: {
  name: string;
  value: number;
  rating?: string;
  delta?: number;
  navigationType?: string;
}) {
  if (!isSampledIn()) return;
  // Legacy FID is never shown and the server no longer stores it — don't spend bytes on it.
  if (metric.name === "FID") return;
  buffer.set(metric.name, {
    name: metric.name,
    value: metric.value,
    rating: metric.rating ?? null,
    delta: metric.delta ?? null,
    navigation_type: metric.navigationType ?? null,
  });

  /**
   * CLS and INP are only finalized when the page is hidden, and web-vitals defers
   * registering its own visibilitychange listener behind an activation guard — so our
   * listener can be registered first and fire first, beaconing a buffer that does not
   * yet contain them. That is not hypothetical: the first real page load recorded
   * FCP/LCP/TTFB/FID and silently dropped CLS and INP.
   *
   * Flushing again whenever a metric lands while the page is already hidden removes the
   * dependency on listener order completely. The send is deduped by signature, so this
   * costs nothing when there is nothing new.
   */
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    flush();
  }
}

let listenersBound = false;
function bindListeners() {
  if (listenersBound || typeof document === "undefined") return;
  listenersBound = true;
  observeLcp();
  // visibilitychange→hidden is the only unload signal that fires reliably on mobile,
  // where the browser can freeze or kill a backgrounded tab without ever firing
  // pagehide/beforeunload. pagehide is kept as a desktop-side belt and braces.
  document.addEventListener(
    "visibilitychange",
    () => {
      if (document.visibilityState === "hidden") flush();
    },
    { capture: true }
  );
  window.addEventListener("pagehide", flush, { capture: true });
  // A BFCache restore is a new page view as far as web-vitals is concerned: it reports a
  // fresh set of metrics stamped 'back-forward-cache'. Start a new page load so they land in
  // their own rows (which the dashboard excludes) instead of overwriting this load's.
  // Everything for the previous load was already sent by the pagehide above.
  window.addEventListener(
    "pageshow",
    (e) => {
      if (!(e as PageTransitionEvent).persisted) return;
      startNewPageLoad();
      buffer.clear();
      lastSentSignature = "";
      context = null;
      lcpElement = null;
      lcpUrl = null;
    },
    { capture: true }
  );
}

export default function WebVitalsReporter() {
  useReportWebVitals(record);
  useEffect(bindListeners, []);
  return null;
}
